/**
 * SSE Hook for Encoder Triage Alerts
 *
 * Connects to the triage SSE endpoint with JWT authentication
 * and provides real-time encoder alert updates.
 *
 * SSE Event Listeners
 * - 'connected'
 * - 'heartbeat'
 * - 'encoder:alert'
 * - 'encoder:alert_update'
 * - 'encoder:alert_resolved'
 * - 'encoder:update'
 * - 'triage:started'
 * - 'triage:ended'
 * - 'encoder:flapping'
 * - 'encoder:flapping:resolved'
 * - 'visibilitychange'
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE_URL } from '@/client/config';
import type {
  SSEEvent,
  SSEConnectedPayload,
  EncoderAlertPayload,
  TriageStartedPayload,
  TriageEndedPayload,
  FlappingAlertPayload,
  FlappingResolvedPayload,
} from '@shared/types';

// ============================================================================
// Types
// ============================================================================

export type SSEConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

/** Payload for alert resolution */
export interface AlertResolvedPayload {
  encoderId: string;
  alertType: string;
}

/** Payload for alert update (lastAnomalyAt refresh) */
export interface AlertUpdatePayload {
  encoderId: string;
  alertType: string;
  lastAnomalyAt: number;
  currentValue?: number;
}

export interface UseTriageSSEOptions {
  /** Auto-connect on mount (default: false - must explicitly connect) */
  autoConnect?: boolean;
  /** Callback when an encoder alert is received */
  onAlert?: (data: EncoderAlertPayload) => void;
  /** Callback when an encoder alert's lastAnomalyAt is updated */
  onAlertUpdate?: (data: AlertUpdatePayload) => void;
  /** Callback when an encoder alert is resolved */
  onAlertResolved?: (data: AlertResolvedPayload) => void;
  /** Callback when triage session starts */
  onTriageStarted?: (data: TriageStartedPayload) => void;
  /** Callback when triage session ends */
  onTriageEnded?: (data: TriageEndedPayload) => void;
  /** Callback when encoder flapping is detected */
  onFlapping?: (data: FlappingAlertPayload) => void;
  /** Callback when encoder flapping is resolved */
  onFlappingResolved?: (data: FlappingResolvedPayload) => void;
  /** Callback for any event */
  onEvent?: (event: SSEEvent) => void;
}

export interface UseTriageSSEReturn {
  status: SSEConnectionStatus;
  connect: () => Promise<void>;
  disconnect: () => void;
  isConnected: boolean;
  lastEvent: SSEEvent | null;
  /** SSE client ID assigned by server */
  clientId: string | null;
}

// ============================================================================
// Hook
// ============================================================================

export function useTriageSSE(options: UseTriageSSEOptions = {}): UseTriageSSEReturn {
  const {
    autoConnect = false,
    onAlert,
    onAlertUpdate,
    onAlertResolved,
    onTriageStarted,
    onTriageEnded,
    onFlapping,
    onFlappingResolved,
    onEvent,
  } = options;

  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const [status, setStatus] = useState<SSEConnectionStatus>('disconnected');
  const [lastEvent, setLastEvent] = useState<SSEEvent | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const intentionalDisconnectRef = useRef(false);
  const isReconnectingRef = useRef(false);
  const wasConnectedBeforeHiddenRef = useRef(false);

  // Store callbacks in refs to avoid recreating connect() on every render
  const onAlertRef = useRef(onAlert);
  const onAlertUpdateRef = useRef(onAlertUpdate);
  const onAlertResolvedRef = useRef(onAlertResolved);
  const onTriageStartedRef = useRef(onTriageStarted);
  const onTriageEndedRef = useRef(onTriageEnded);
  const onFlappingRef = useRef(onFlapping);
  const onFlappingResolvedRef = useRef(onFlappingResolved);
  const onEventRef = useRef(onEvent);

  // Keep refs updated
  useEffect(() => {
    onAlertRef.current = onAlert;
    onAlertUpdateRef.current = onAlertUpdate;
    onAlertResolvedRef.current = onAlertResolved;
    onTriageStartedRef.current = onTriageStarted;
    onTriageEndedRef.current = onTriageEnded;
    onFlappingRef.current = onFlapping;
    onFlappingResolvedRef.current = onFlappingResolved;
    onEventRef.current = onEvent;
  }, [onAlert, onAlertUpdate, onAlertResolved, onTriageStarted, onTriageEnded, onFlapping, onFlappingResolved, onEvent]);

  // Max reconnect delay: 30 seconds
  const MAX_RECONNECT_DELAY = 30000;
  const BASE_RECONNECT_DELAY = 1000;

  const disconnect = useCallback(() => {
    intentionalDisconnectRef.current = true;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setStatus('disconnected');
    setClientId(null);
    reconnectAttempts.current = 0;
  }, []);

  const connect = useCallback(async () => {
    // Reset intentional disconnect flag
    intentionalDisconnectRef.current = false;

    // Don't connect if not authenticated
    if (!isAuthenticated) {
      console.warn('[TriageSSE] Cannot connect - user not authenticated');
      return;
    }

    // Clean up existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    setStatus('connecting');

    try {
      // Get fresh access token
      const token = await getAccessTokenSilently();

      // Create SSE connection with token in query param
      const url = `${API_BASE_URL}/api/encoders/triage/stream?token=${encodeURIComponent(token)}`;
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      // Handle connection open
      eventSource.onopen = () => {
        console.info('[TriageSSE] Connection opened');
        setStatus('connected');
        reconnectAttempts.current = 0;
      };

      // Handle errors
      eventSource.onerror = (error) => {
        // Ignore errors from intentional disconnects (e.g., Strict Mode unmount)
        if (intentionalDisconnectRef.current) {
          return;
        }

        // Use warn instead of error to avoid Parcel error overlay treating this as a crash
        console.warn('[TriageSSE] Connection error (will reconnect):', error);

        // EventSource readyState: 0 = CONNECTING, 1 = OPEN, 2 = CLOSED
        if (eventSource.readyState === EventSource.CLOSED) {
          setStatus('error');

          // Prevent multiple simultaneous reconnect attempts
          if (isReconnectingRef.current) {
            return;
          }

          // Don't auto-reconnect after too many failures (likely auth issue)
          if (reconnectAttempts.current >= 5) {
            console.info('[TriageSSE] Max reconnect attempts reached, stopping');
            return;
          }

          // Clear any pending reconnect before scheduling new one
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }

          isReconnectingRef.current = true;

          // Exponential backoff reconnect
          const delay = Math.min(
            BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttempts.current),
            MAX_RECONNECT_DELAY
          );
          reconnectAttempts.current++;

          console.info(`[TriageSSE] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);
          reconnectTimeoutRef.current = setTimeout(() => {
            isReconnectingRef.current = false;
            connect();
          }, delay);
        }
      };

      // Handle 'connected' event
      eventSource.addEventListener('connected', (e: MessageEvent) => { 
        try {
          const event: SSEEvent<SSEConnectedPayload> = JSON.parse(e.data);
          setClientId(event.data.clientId);
          setLastEvent(event);
          onEventRef.current?.(event);
        } catch (err) {
          console.error('[TriageSSE] Failed to parse connected event:', err);
        }
      });

      // Handle 'heartbeat' event (don't update lastEvent to reduce noise)
      eventSource.addEventListener('heartbeat', () => { 
        // Heartbeats are just for keeping connection alive, no action needed
      });

      // Handle 'encoder:alert' event
      eventSource.addEventListener('encoder:alert', (e: MessageEvent) => {
        try {
          const event: SSEEvent<EncoderAlertPayload> = JSON.parse(e.data);
          setLastEvent(event);
          onEventRef.current?.(event);
          onAlertRef.current?.(event.data);
        } catch (err) {
          console.error('[TriageSSE] Failed to parse encoder:alert:', err);
        }
      });

      // Handle 'encoder:alert_update' event (lastAnomalyAt refresh)
      eventSource.addEventListener('encoder:alert_update', (e: MessageEvent) => {
        try {
          const event: SSEEvent<AlertUpdatePayload> = JSON.parse(e.data);
          setLastEvent(event);
          onEventRef.current?.(event);
          onAlertUpdateRef.current?.(event.data);
        } catch (err) {
          console.error('[TriageSSE] Failed to parse encoder:alert_update:', err);
        }
      });

      // Handle 'encoder:alert_resolved' event
      eventSource.addEventListener('encoder:alert_resolved', (e: MessageEvent) => {
        try {
          const event: SSEEvent<AlertResolvedPayload> = JSON.parse(e.data);
          setLastEvent(event);
          onEventRef.current?.(event);
          onAlertResolvedRef.current?.(event.data);
        } catch (err) {
          console.error('[TriageSSE] Failed to parse encoder:alert_resolved:', err);
        }
      });

      // Handle 'encoder:update' event
      eventSource.addEventListener('encoder:update', (e: MessageEvent) => { 
        try {
          const event: SSEEvent = JSON.parse(e.data);
          setLastEvent(event);
          onEventRef.current?.(event);
        } catch (err) {
          console.error('[TriageSSE] Failed to parse encoder:update:', err);
        }
      });

      // Handle 'triage:started' event
      eventSource.addEventListener('triage:started', (e: MessageEvent) => { 
        try {
          const event: SSEEvent<TriageStartedPayload> = JSON.parse(e.data);
          setLastEvent(event);
          onEventRef.current?.(event);
          onTriageStartedRef.current?.(event.data);
        } catch (err) {
          console.error('[TriageSSE] Failed to parse triage:started:', err);
        }
      });

      // Handle 'triage:ended' event
      eventSource.addEventListener('triage:ended', (e: MessageEvent) => { 
        try {
          const event: SSEEvent<TriageEndedPayload> = JSON.parse(e.data);
          setLastEvent(event);
          onEventRef.current?.(event);
          onTriageEndedRef.current?.(event.data);
        } catch (err) {
          console.error('[TriageSSE] Failed to parse triage:ended:', err);
        }
      });

      // Handle 'encoder:flapping' event
      eventSource.addEventListener('encoder:flapping', (e: MessageEvent) => { 
        try {
          const event: SSEEvent<FlappingAlertPayload> = JSON.parse(e.data);
          setLastEvent(event);
          onEventRef.current?.(event);
          onFlappingRef.current?.(event.data);
        } catch (err) {
          console.error('[TriageSSE] Failed to parse encoder:flapping:', err);
        }
      });

      // Handle 'encoder:flapping:resolved' event
      eventSource.addEventListener('encoder:flapping:resolved', (e: MessageEvent) => { 
        try {
          const event: SSEEvent<FlappingResolvedPayload> = JSON.parse(e.data);
          setLastEvent(event);
          onEventRef.current?.(event);
          onFlappingResolvedRef.current?.(event.data);
        } catch (err) {
          console.error('[TriageSSE] Failed to parse encoder:flapping:resolved:', err);
        }
      });
    } catch (err) {
      console.error('[TriageSSE] Failed to connect:', err);
      setStatus('error');
    }
  }, [isAuthenticated, getAccessTokenSilently]); // Removed callback deps - using refs now

  // Auto-connect on mount if enabled and authenticated
  // Small delay allows page to settle and avoids React 18 Strict Mode interruptions
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    if (autoConnect && isAuthenticated) {
      timeoutId = setTimeout(() => {
        connect();
      }, 100);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      disconnect();
    };
  }, [autoConnect, isAuthenticated, connect, disconnect]);

  // Handle visibility change (sleep/wake, tab switch)
  // Disconnect when hidden, reconnect when visible to avoid stale connections
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page hidden (sleep, tab switch) - remember if we were connected, then disconnect
        wasConnectedBeforeHiddenRef.current = eventSourceRef.current !== null;
        if (eventSourceRef.current) {
          // console.info('[TriageSSE] Page hidden, disconnecting');
          disconnect();
        }
      } else {
        // Page visible again - reconnect if we were connected before hiding
        if (wasConnectedBeforeHiddenRef.current && isAuthenticated && !eventSourceRef.current) {
          // console.info('[TriageSSE] Page visible, reconnecting');
          // Reset reconnect state for fresh start
          reconnectAttempts.current = 0;
          isReconnectingRef.current = false;
          wasConnectedBeforeHiddenRef.current = false;
          // Small delay to let system settle after wake
          setTimeout(() => {
            connect();
          }, 500);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange); 
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, connect, disconnect]);

  return {
    status,
    connect,
    disconnect,
    isConnected: status === 'connected',
    lastEvent,
    clientId,
  };
}
